import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Search,
  RefreshCw,
  Upload,
  Database,
  TrendingUp,
  Users,
  Calendar,
  BarChart3,
  Play,
  FileUp,
  AlertCircle,
  CheckCircle,
  Loader2,
  Settings,
  Zap,
  FileCheck,
  Shield,
  Clock,
  Pause,
  Trash2,
  Eye,
  Check,
  X,
} from 'lucide-react'
import { PageLoader } from '@/components/common/LoadingSpinner'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { scoutApi, type ScoutRunStats, type JobResultsResponse, type PreviewCarrier } from '@/api/endpoints/scout'

const SOURCE_COLORS: Record<string, string> = {
  fmcsa: 'bg-blue-500',
  scout_api: 'bg-green-500',
  manual: 'bg-purple-500',
  csv_import: 'bg-orange-500',
  webtel: 'bg-cyan-500',
  riviera: 'bg-indigo-500',
  unknown: 'bg-gray-500',
}

const PRIORITY_COLORS: Record<string, string> = {
  high: 'bg-red-500',
  medium: 'bg-yellow-500',
  low: 'bg-green-500',
}

type ScoutRunType = 'all' | 'newly_applied' | 'newly_approved'
  | 'approved_30_days' | 'approved_60_days' | 'approved_90_days' | 'approved_180_days'
  | 'applied_30_days' | 'applied_60_days' | 'applied_90_days' | 'applied_180_days'

function getScoutRunLabel(type: ScoutRunType): string {
  const labels: Record<ScoutRunType, string> = {
    all: 'All Carriers',
    newly_applied: 'Newly Applied',
    newly_approved: 'Newly Approved',
    approved_30_days: 'Approved 30 Days',
    approved_60_days: 'Approved 60 Days',
    approved_90_days: 'Approved 90 Days',
    approved_180_days: 'Approved 180 Days',
    applied_30_days: 'Applied 30 Days',
    applied_60_days: 'Applied 60 Days',
    applied_90_days: 'Applied 90 Days',
    applied_180_days: 'Applied 180 Days',
  }
  return labels[type]
}

function getApiType(type: ScoutRunType): 'all' | 'newly_applied' | 'newly_approved' {
  if (type.startsWith('approved') || type === 'newly_approved') return 'newly_approved'
  if (type.startsWith('applied') || type === 'newly_applied') return 'newly_applied'
  return 'all'
}

export function Scout() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [isIngestDialogOpen, setIsIngestDialogOpen] = useState(false)
  const [isRunDialogOpen, setIsRunDialogOpen] = useState(false)
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false)

  // Run scout form state
  const [runType, setRunType] = useState<ScoutRunType>('all')
  const [dateStart, setDateStart] = useState<string>(() => {
    const d = new Date(); d.setDate(d.getDate() - 30)
    return d.toISOString().split('T')[0]
  })
  const [dateEnd, setDateEnd] = useState<string>(() => new Date().toISOString().split('T')[0])
  const [minQualityScore, setMinQualityScore] = useState<number>(0)
  const [runResult, setRunResult] = useState<ScoutRunStats | null>(null)
  const [isRunning, setIsRunning] = useState(false)
  const [previewData, setPreviewData] = useState<PreviewCarrier[] | null>(null)
  const [previewTotal, setPreviewTotal] = useState<number>(0)
  const [isPreviewing, setIsPreviewing] = useState(false)

  // Config form state
  const [configWaitDays, setConfigWaitDays] = useState<number>(7)

  // Scheduler job dialogs
  const [isResultsDialogOpen, setIsResultsDialogOpen] = useState(false)
  const [selectedJobForResults, setSelectedJobForResults] = useState<string | null>(null)
  const [jobResults, setJobResults] = useState<JobResultsResponse | null>(null)
  const [isLoadingResults, setIsLoadingResults] = useState(false)

  // Manual ingest form state
  const [ingestForm, setIngestForm] = useState({
    company_name: '',
    mc_number: '',
    dot_number: '',
    phone: '',
    email: '',
    city: '',
    state: '',
    zip_code: '',
    source: 'manual',
  })

  // Fetch scout config
  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['scout', 'config'],
    queryFn: () => scoutApi.getConfig().then(res => res.data),
    refetchInterval: 60000,
  })

  // Fetch scout stats
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['scout', 'stats'],
    queryFn: () => scoutApi.getStats().then(res => res.data),
    refetchInterval: 30000,
  })

  // Fetch scheduler jobs
  const { data: schedulerJobs, isLoading: isLoadingJobs, refetch: refetchJobs } = useQuery({
    queryKey: ['scout', 'scheduler', 'jobs'],
    queryFn: () => scoutApi.getSchedulerJobs().then(res => res.data),
    refetchInterval: 60000,
  })

  // Pause job mutation
  const pauseJobMutation = useMutation({
    mutationFn: (jobName: string) => scoutApi.pauseSchedulerJob(jobName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scout', 'scheduler', 'jobs'] })
    },
  })

  // Resume job mutation
  const resumeJobMutation = useMutation({
    mutationFn: (jobName: string) => scoutApi.resumeSchedulerJob(jobName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scout', 'scheduler', 'jobs'] })
    },
  })

  // Run job now mutation
  const runJobNowMutation = useMutation({
    mutationFn: (jobName: string) => scoutApi.runSchedulerJobNow(jobName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scout', 'scheduler', 'jobs'] })
      queryClient.invalidateQueries({ queryKey: ['scout', 'stats'] })
    },
  })

  // Delete job mutation
  const deleteJobMutation = useMutation({
    mutationFn: (jobName: string) => scoutApi.deleteSchedulerJob(jobName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scout', 'scheduler', 'jobs'] })
    },
  })

  // Update config mutation
  const updateConfigMutation = useMutation({
    mutationFn: (newConfig: { wait_days: number }) => scoutApi.updateConfig(newConfig),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['scout', 'config'] })
      queryClient.invalidateQueries({ queryKey: ['scout', 'stats'] })
      setIsConfigDialogOpen(false)
      toast({
        title: 'Configuration Updated',
        description: `Wait days set to ${response.data.config?.wait_days ?? configWaitDays}`,
      })
    },
    onError: (error: Error) => {
      console.error('Failed to update config:', error)
      toast({
        title: 'Error',
        description: error.message || 'Failed to update configuration',
        variant: 'destructive',
      })
    },
  })

  // View job results handler
  const handleViewResults = async (jobName: string) => {
    setSelectedJobForResults(jobName)
    setIsResultsDialogOpen(true)
    setIsLoadingResults(true)
    try {
      const response = await scoutApi.getJobResults(jobName)
      setJobResults(response.data)
    } catch (err) {
      console.error('Failed to fetch job results:', err)
    } finally {
      setIsLoadingResults(false)
    }
  }

  // Run scout mutation
  const runScoutMutation = useMutation({
    mutationFn: async ({ type, params }: { type: ScoutRunType; params: { min_quality_score?: number; date_start?: string; date_end?: string; source_type?: string } }) => {
      setIsRunning(true)
      setRunResult(null)

      let response
      const apiType = getApiType(type)
      if (apiType === 'newly_applied') {
        response = await scoutApi.runScoutNewlyApplied(params)
      } else if (apiType === 'newly_approved') {
        response = await scoutApi.runScoutNewlyApproved(params)
      } else {
        response = await scoutApi.runScout(params)
      }
      return response.data
    },
    onSuccess: (data) => {
      setIsRunning(false)
      setRunResult(data.stats)
      queryClient.invalidateQueries({ queryKey: ['scout'] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
    onError: () => {
      setIsRunning(false)
    },
  })

  // Ingest mutation
  const ingestMutation = useMutation({
    mutationFn: (data: typeof ingestForm) => scoutApi.ingestLead(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scout'] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      setIsIngestDialogOpen(false)
      resetIngestForm()
    },
  })

  const resetIngestForm = () => {
    setIngestForm({
      company_name: '',
      mc_number: '',
      dot_number: '',
      phone: '',
      email: '',
      city: '',
      state: '',
      zip_code: '',
      source: 'manual',
    })
  }

  const handleRunScout = () => {
    const params: { min_quality_score?: number; date_start?: string; date_end?: string; source_type?: string } = {
      min_quality_score: minQualityScore,
      date_start: dateStart,
      date_end: dateEnd,
      source_type: runType,
    }
    runScoutMutation.mutate({ type: runType, params })
  }

  const handlePreview = async () => {
    setIsPreviewing(true)
    setPreviewData(null)
    setRunResult(null)
    try {
      const response = await scoutApi.previewScout({
        date_start: dateStart,
        date_end: dateEnd,
        source_type: runType,
      })
      setPreviewData(response.data.carriers)
      setPreviewTotal(response.data.total)
    } catch (err) {
      console.error('Preview failed:', err)
      toast({
        title: 'Preview Failed',
        description: err instanceof Error ? err.message : 'Failed to fetch preview data',
        variant: 'destructive',
      })
    } finally {
      setIsPreviewing(false)
    }
  }

  const handleIngestSubmit = () => {
    if (!ingestForm.company_name && !ingestForm.mc_number) {
      return
    }
    ingestMutation.mutate(ingestForm)
  }

  if (isLoading || configLoading) {
    return <PageLoader />
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <p className="text-lg font-medium">Failed to load Scout configuration</p>
        <p className="text-sm text-muted-foreground">Please try again later</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => queryClient.invalidateQueries({ queryKey: ['scout'] })}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Scout Agent Configuration</h1>
          <p className="text-muted-foreground">
            Configure Riviera Finance lead scouting
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => {
            setConfigWaitDays(config?.wait_days ?? 7)
            setIsConfigDialogOpen(true)
          }}>
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button variant="outline" onClick={() => setIsIngestDialogOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Manual Ingest
          </Button>
          <Button onClick={() => setIsRunDialogOpen(true)}>
            <Play className="h-4 w-4 mr-2" />
            Run Scout Now
          </Button>
        </div>
      </div>

      {/* Current Configuration Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Current Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Wait Days</p>
              <p className="text-2xl font-bold">{config?.wait_days ?? 7}</p>
              <p className="text-xs text-muted-foreground mt-1">Days to wait before scouting Riviera carriers</p>
            </div>
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Last Run</p>
              <p className="text-lg font-semibold">
                {stats?.lastRun?.time ? new Date(stats.lastRun.time).toLocaleString() : 'Never'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {stats?.lastRun?.stats?.total_created !== undefined
                  ? `${stats.lastRun.stats.total_created} leads created`
                  : stats?.lastRun?.outcome || '-'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_leads?.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">Active leads in system</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Leads Today</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.leads_today || 0}</div>
            <p className="text-xs text-muted-foreground">Discovered today</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.leads_this_week || 0}</div>
            <p className="text-xs text-muted-foreground">Last 7 days</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Avg Quality Score</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.average_quality_score?.toFixed(1) || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.enrichment_rate?.toFixed(0) || 0}% enriched
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown Cards */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* By Source */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Leads by Source
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.leads_by_source && Object.keys(stats.leads_by_source).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.leads_by_source)
                  .sort((a, b) => b[1] - a[1])
                  .map(([source, count]) => {
                    const total = stats.total_leads || 1
                    const percentage = ((count / total) * 100).toFixed(1)
                    return (
                      <div key={source} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="capitalize font-medium">{source.replace('_', ' ')}</span>
                          <span className="text-muted-foreground">
                            {count.toLocaleString()} ({percentage}%)
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className={cn('h-full rounded-full', SOURCE_COLORS[source] || 'bg-gray-500')}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No lead data available</p>
            )}
          </CardContent>
        </Card>

        {/* By Priority */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Leads by Priority
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.leads_by_priority && Object.keys(stats.leads_by_priority).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.leads_by_priority)
                  .sort((a, b) => {
                    const order = { high: 0, medium: 1, low: 2 }
                    return (order[a[0] as keyof typeof order] || 3) - (order[b[0] as keyof typeof order] || 3)
                  })
                  .map(([priority, count]) => {
                    const total = stats.total_leads || 1
                    const percentage = ((count / total) * 100).toFixed(1)
                    return (
                      <div key={priority} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <Badge
                              className={cn(
                                'text-white text-xs',
                                priority === 'high' ? 'bg-red-500' : priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                              )}
                            >
                              {priority}
                            </Badge>
                          </div>
                          <span className="text-muted-foreground">
                            {count.toLocaleString()} ({percentage}%)
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className={cn('h-full rounded-full', PRIORITY_COLORS[priority] || 'bg-gray-500')}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No priority data available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Scheduled Jobs Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Scheduled Jobs
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Manage automated scouting schedules</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-sm">
              {schedulerJobs?.jobs?.filter(j => j.state === 'ENABLED').length || 0} Active
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchJobs()}
              disabled={isLoadingJobs}
            >
              <RefreshCw className={cn("h-4 w-4 mr-2", isLoadingJobs && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingJobs ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : schedulerJobs?.jobs && schedulerJobs.jobs.length > 0 ? (
            <div className="space-y-4">
              {schedulerJobs.jobs.map((job) => (
                <div
                  key={job.name}
                  className="border rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{job.name}</span>
                      <Badge
                        variant={job.state === 'ENABLED' ? 'default' : 'secondary'}
                        className={cn(
                          job.state === 'ENABLED' && 'bg-green-100 text-green-800',
                          job.state === 'PAUSED' && 'bg-yellow-100 text-yellow-800',
                          job.state === 'DISABLED' && 'bg-gray-100 text-gray-800'
                        )}
                      >
                        {job.state === 'ENABLED' && <Check className="h-3 w-3 mr-1" />}
                        {job.state === 'PAUSED' && <Pause className="h-3 w-3 mr-1" />}
                        {job.state}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      {job.state === 'ENABLED' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => pauseJobMutation.mutate(job.name)}
                          disabled={pauseJobMutation.isPending}
                        >
                          <Pause className="h-4 w-4 mr-1" />
                          Pause
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => resumeJobMutation.mutate(job.name)}
                          disabled={resumeJobMutation.isPending}
                          className="text-green-600 hover:text-green-700"
                        >
                          <Play className="h-4 w-4 mr-1" />
                          Resume
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewResults(job.name)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View Results
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => runJobNowMutation.mutate(job.name)}
                        disabled={runJobNowMutation.isPending}
                      >
                        <Play className="h-4 w-4 mr-1" />
                        Run Now
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm(`Are you sure you want to delete the job "${job.name}"? This cannot be undone.`)) {
                            deleteJobMutation.mutate(job.name)
                          }
                        }}
                        disabled={deleteJobMutation.isPending}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{job.description}</p>
                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{job.schedule} ({job.time_zone})</span>
                    </div>
                    {job.next_run && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>Next: {new Date(job.next_run).toLocaleString()}</span>
                      </div>
                    )}
                    {job.last_run && (
                      <div className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        <span>Last: {new Date(job.last_run).toLocaleString()}</span>
                        {job.last_status_code === 0 && (
                          <Badge variant="outline" className="ml-1 text-green-600 border-green-200">
                            <Check className="h-3 w-3 mr-1" />
                            Success
                          </Badge>
                        )}
                        {job.last_status_code !== 0 && job.last_status_code !== undefined && job.last_status_code !== -1 && (
                          <Badge variant="outline" className="ml-1 text-red-600 border-red-200">
                            <X className="h-3 w-3 mr-1" />
                            Error
                          </Badge>
                        )}
                      </div>
                    )}
                    {!job.last_run && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>Last: Never run</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No scheduled jobs found</p>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => {
                setRunType('all')
                setIsRunDialogOpen(true)
              }}
            >
              <Database className="h-6 w-6 text-blue-500" />
              <div className="text-center">
                <p className="font-medium">Run All Carriers</p>
                <p className="text-xs text-muted-foreground">Scout all eligible</p>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => {
                setRunType('newly_applied')
                setIsRunDialogOpen(true)
              }}
            >
              <FileCheck className="h-6 w-6 text-green-500" />
              <div className="text-center">
                <p className="font-medium">Newly Applied</p>
                <p className="text-xs text-muted-foreground">Recent applications</p>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => {
                setRunType('newly_approved')
                setIsRunDialogOpen(true)
              }}
            >
              <Shield className="h-6 w-6 text-purple-500" />
              <div className="text-center">
                <p className="font-medium">Newly Approved</p>
                <p className="text-xs text-muted-foreground">Recent approvals</p>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => setIsIngestDialogOpen(true)}
            >
              <FileUp className="h-6 w-6 text-orange-500" />
              <div className="text-center">
                <p className="font-medium">Manual Entry</p>
                <p className="text-xs text-muted-foreground">Add single lead</p>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Run Scout Dialog */}
      <Dialog open={isRunDialogOpen} onOpenChange={(open) => {
        setIsRunDialogOpen(open)
        if (!open) {
          setPreviewData(null)
          setPreviewTotal(0)
          setRunResult(null)
        }
      }}>
        <DialogContent className={previewData ? "max-w-4xl max-h-[85vh] overflow-y-auto" : "max-w-lg"}>
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-blue-600 text-white">
              <Play className="h-6 w-6" />
              <DialogTitle className="text-white">Run Scout Now</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-muted-foreground">
              Trigger an immediate scout run for Riviera Finance carriers
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date Start</Label>
                <Input
                  type="date"
                  value={dateStart}
                  onChange={(e) => setDateStart(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Start of date range to scout
                </p>
              </div>

              <div>
                <Label>Date End</Label>
                <Input
                  type="date"
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  End of date range to scout
                </p>
              </div>
            </div>

            <div>
              <Label>Minimum Quality Score</Label>
              <Input
                type="number"
                value={minQualityScore}
                onChange={(e) => setMinQualityScore(parseInt(e.target.value) || 0)}
                min={0}
                max={100}
                className="w-32"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Only accept leads with score above this threshold
              </p>
            </div>

            <div>
              <Label>Scout Type</Label>
              <Select value={runType} onValueChange={(v: ScoutRunType) => setRunType(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Carriers</SelectItem>
                  <SelectItem value="newly_applied">Newly Applied</SelectItem>
                  <SelectItem value="newly_approved">Newly Approved</SelectItem>
                  <SelectItem value="approved_30_days">Approved 30 Days</SelectItem>
                  <SelectItem value="approved_60_days">Approved 60 Days</SelectItem>
                  <SelectItem value="approved_90_days">Approved 90 Days</SelectItem>
                  <SelectItem value="approved_180_days">Approved 180 Days</SelectItem>
                  <SelectItem value="applied_30_days">Applied 30 Days</SelectItem>
                  <SelectItem value="applied_60_days">Applied 60 Days</SelectItem>
                  <SelectItem value="applied_90_days">Applied 90 Days</SelectItem>
                  <SelectItem value="applied_180_days">Applied 180 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Preview Loading */}
            {isPreviewing && (
              <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
                  <p className="text-sm font-medium">Fetching preview data...</p>
                </div>
              </div>
            )}

            {/* Preview Results Table */}
            {previewData && !isPreviewing && !runResult && (
              <div className="border rounded-lg">
                <div className="p-3 bg-muted/50 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4 text-blue-500" />
                    <span className="font-medium text-sm">Preview Results</span>
                    <Badge variant="secondary" className="font-bold">{previewTotal} total</Badge>
                  </div>
                  <Badge variant="outline">{getScoutRunLabel(runType)}</Badge>
                </div>
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-background border-b">
                      <tr>
                        <th className="text-left p-2 font-medium">Company</th>
                        <th className="text-left p-2 font-medium">MC#</th>
                        <th className="text-left p-2 font-medium">Phone</th>
                        <th className="text-left p-2 font-medium">Location</th>
                        <th className="text-left p-2 font-medium">Sales Rep</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((carrier, idx) => (
                        <tr key={idx} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="p-2 max-w-[200px] truncate">{carrier.company_name || '-'}</td>
                          <td className="p-2 font-mono text-xs">{carrier.mc_number || '-'}</td>
                          <td className="p-2 text-xs">{carrier.phone || '-'}</td>
                          <td className="p-2 text-xs">{[carrier.city, carrier.state].filter(Boolean).join(', ') || '-'}</td>
                          <td className="p-2 text-xs max-w-[150px] truncate">{carrier.sales_rep_name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Run Progress */}
            {isRunning && (
              <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
                  <p className="text-sm font-medium">Running scout...</p>
                </div>
              </div>
            )}

            {/* Run Results */}
            {runResult && !isRunning && (
              <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
                <h3 className="font-bold text-green-800 dark:text-green-200 mb-2">Scout Run Complete</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Processed</p>
                    <p className="text-xl font-bold">{runResult.total_processed || runResult.processed || 0}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Created</p>
                    <p className="text-xl font-bold text-green-600">{runResult.total_created || runResult.created || 0}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Duplicates</p>
                    <p className="text-xl font-bold text-blue-600">{runResult.total_duplicates || runResult.duplicates || 0}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Rejected</p>
                    <p className="text-xl font-bold text-orange-600">{runResult.total_rejected || runResult.rejected || 0}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsRunDialogOpen(false)
              setRunResult(null)
              setPreviewData(null)
            }}>
              {runResult ? 'Close' : 'Cancel'}
            </Button>
            {!runResult && (
              <>
                <Button variant="secondary" onClick={handlePreview} disabled={isRunning || isPreviewing}>
                  {isPreviewing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </>
                  )}
                </Button>
                <Button onClick={handleRunScout} disabled={isRunning || isPreviewing}>
                  {isRunning ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Run {getScoutRunLabel(runType)}
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Configuration Dialog */}
      <Dialog open={isConfigDialogOpen} onOpenChange={setIsConfigDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-gray-600 text-white">
              <Settings className="h-6 w-6" />
              <DialogTitle className="text-white">Configuration Settings</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-muted-foreground">
              Update default Scout agent settings
            </p>

            <div>
              <Label>Wait Days</Label>
              <Input
                type="number"
                value={configWaitDays}
                onChange={(e) => setConfigWaitDays(parseInt(e.target.value) || 0)}
                min={0}
                max={365}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Number of days to wait before scouting new carriers (0 = today, 7 = scout carriers from 7 days ago)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfigDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                console.log('Save clicked, wait_days:', configWaitDays)
                updateConfigMutation.mutate({ wait_days: configWaitDays })
              }}
              disabled={updateConfigMutation.isPending}
            >
              {updateConfigMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Configuration'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Ingest Dialog */}
      <Dialog open={isIngestDialogOpen} onOpenChange={setIsIngestDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-purple-600 text-white">
              <Upload className="h-6 w-6" />
              <DialogTitle className="text-white">Manual Lead Entry</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Company Name *</Label>
                <Input
                  value={ingestForm.company_name}
                  onChange={(e) => setIngestForm(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="ABC Trucking LLC"
                />
              </div>

              <div>
                <Label>MC Number</Label>
                <Input
                  value={ingestForm.mc_number}
                  onChange={(e) => setIngestForm(prev => ({ ...prev, mc_number: e.target.value }))}
                  placeholder="123456"
                />
              </div>

              <div>
                <Label>DOT Number</Label>
                <Input
                  value={ingestForm.dot_number}
                  onChange={(e) => setIngestForm(prev => ({ ...prev, dot_number: e.target.value }))}
                  placeholder="654321"
                />
              </div>

              <div>
                <Label>Phone</Label>
                <Input
                  value={ingestForm.phone}
                  onChange={(e) => setIngestForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="714-470-1815"
                />
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={ingestForm.email}
                  onChange={(e) => setIngestForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="contact@company.com"
                />
              </div>

              <div>
                <Label>City</Label>
                <Input
                  value={ingestForm.city}
                  onChange={(e) => setIngestForm(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="Los Angeles"
                />
              </div>

              <div>
                <Label>State</Label>
                <Input
                  value={ingestForm.state}
                  onChange={(e) => setIngestForm(prev => ({ ...prev, state: e.target.value }))}
                  placeholder="CA"
                  maxLength={2}
                />
              </div>

              <div>
                <Label>ZIP Code</Label>
                <Input
                  value={ingestForm.zip_code}
                  onChange={(e) => setIngestForm(prev => ({ ...prev, zip_code: e.target.value }))}
                  placeholder="90001"
                />
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium">Automatic Processing</p>
                  <p className="text-muted-foreground">
                    Lead will be deduplicated, enriched, and scored automatically
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsIngestDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleIngestSubmit}
              disabled={ingestMutation.isPending || (!ingestForm.company_name && !ingestForm.mc_number)}
            >
              {ingestMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Ingest Lead
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Results Dialog */}
      <Dialog open={isResultsDialogOpen} onOpenChange={setIsResultsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-blue-600 text-white">
              <BarChart3 className="h-6 w-6" />
              <DialogTitle className="text-white">
                Job Results: {selectedJobForResults}
              </DialogTitle>
            </div>
          </DialogHeader>

          {isLoadingResults ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : jobResults ? (
            <div className="space-y-6 max-h-[60vh] overflow-y-auto">
              {/* Summary Stats */}
              {jobResults.data.type === 'riviera_scout' && (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-muted/50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold">{jobResults.data.total_leads_created || 0}</p>
                      <p className="text-xs text-muted-foreground">Total Leads Created</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold">{jobResults.data.leads_last_24h || 0}</p>
                      <p className="text-xs text-muted-foreground">Last 24 Hours</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold">{jobResults.data.total_runs || 0}</p>
                      <p className="text-xs text-muted-foreground">Total Runs</p>
                    </div>
                  </div>

                  {/* Recent Runs */}
                  {jobResults.data.recent_runs && jobResults.data.recent_runs.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3">Recent Runs</h4>
                      <div className="space-y-2">
                        {jobResults.data.recent_runs.slice(0, 10).map((run) => (
                          <div
                            key={run.id}
                            className="flex items-center justify-between p-3 border rounded-lg text-sm"
                          >
                            <div className="flex items-center gap-3">
                              <Badge
                                variant={run.outcome === 'success' ? 'default' : 'destructive'}
                                className={cn(
                                  run.outcome === 'success' && 'bg-green-100 text-green-800',
                                  run.outcome === 'error' && 'bg-red-100 text-red-800'
                                )}
                              >
                                {run.outcome}
                              </Badge>
                              <span className="text-muted-foreground">
                                {new Date(run.created_at).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-muted-foreground">
                              <span>{run.leads_created} created</span>
                              <span>{run.duplicates} dupes</span>
                              <span>{run.leads_processed} processed</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {jobResults.data.type === 'fmcsa_download' && (
                <div className="space-y-4">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground mb-2">Data Directory</p>
                    <p className="font-mono text-sm">{jobResults.data.data_directory}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted/50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold">{jobResults.data.total_carrier_files || 0}</p>
                      <p className="text-xs text-muted-foreground">Carrier Files</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold">{jobResults.data.total_insurance_files || 0}</p>
                      <p className="text-xs text-muted-foreground">Insurance Files</p>
                    </div>
                  </div>
                </div>
              )}

              {jobResults.last_run && (
                <p className="text-xs text-muted-foreground text-center">
                  Last run: {new Date(jobResults.last_run).toLocaleString()}
                </p>
              )}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No results available</p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResultsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
